import axiosInstance from '../utils/axiosInstance';

export interface UserPreference {
    preference_type: string;
    preference_value: string;
    is_active: boolean;
}

export interface TagPreference {
    tag: string;
    is_preferred: boolean;
}

export interface CategoryTags {
    category: string;
    tags: string[];
}

export interface UserPreferencesResponse {
    preferences: UserPreference[];
    total: number;
}

export interface CreateUserPreferenceRequest {
    preference_type: string;
    preference_value: string;
    tag_group_id?: number;
}

const UserPreferencesService = {
    getUserPreferences: async (): Promise<UserPreferencesResponse> => {
        try {
            const response = await axiosInstance.get('/user-preferences');
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch user preferences');
        }
    },

    addUserPreference: async (preference: CreateUserPreferenceRequest): Promise<any> => {
        try {
            const response = await axiosInstance.post('/user-preferences', preference);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to add user preference');
        }
    },

    disableUserPreference: async (type: string, value: string): Promise<any> => {
        try {
            const response = await axiosInstance.patch(`/user-preferences/${type}/${value}/disable`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to disable user preference');
        }
    },

    removeUserPreference: async (type: string, value: string): Promise<any> => {
        try {
            const response = await axiosInstance.delete(`/user-preferences/${type}/${value}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to remove user preference');
        }
    },

    getUserTagPreferences: async (): Promise<TagPreference[]> => {
        try {
            const response = await axiosInstance.get('/user-preferences/tags');
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch user tag preferences');
        }
    },

    getPopularTags: async (): Promise<CategoryTags[]> => {
        try {
            const response = await axiosInstance.get('/user-preferences/popular-tags');
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch popular tags');
        }
    },

    searchTags: async (searchTerm: string): Promise<string[]> => {
        try {
            const response = await axiosInstance.get('/user-preferences/search-tags', {
                params: { q: searchTerm },
            });
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to search tags');
        }
    },

    getPersonalizedNews: async (page: number, pageSize: number): Promise<any> => {
        try {
            const response = await axiosInstance.get('/user-preferences/news', {
                params: { page, pageSize },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch personalized news');
        }
    },
};

export default UserPreferencesService;
